import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { authService, User, AuthError } from "../services/auth/authService";
import { useNavigate } from "react-router-dom";
import { demoService } from "../services/demo/demoService";
import { supabase } from "../services/supabase/client";
import {
  clearCachedSupabaseSession,
  getCachedUser,
} from "../services/supabase/authStorage";
import { plaidService } from "../services/external/plaidService";
import { debugOnce, debugThrottled } from "../lib";

interface AuthContextType {
  user: User | null;
  loading: boolean;
  loadingLog: LoadingLogEntry[];
  signUp: (
    email: string,
    password: string,
    firstName?: string,
    lastName?: string
  ) => Promise<{
    error: AuthError | null;
    requiresEmailConfirmation: boolean;
  }>;
  signIn: (
    email: string,
    password: string
  ) => Promise<{ error: AuthError | null }>;
  signInDemo: () => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  updateProfile: (updates: {
    firstName?: string;
    lastName?: string;
    avatarUrl?: string;
  }) => Promise<{ error: AuthError | null }>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: AuthError | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface LoadingLogEntry {
  timestamp: string;
  message: string;
  data?: Record<string, unknown>;
}

const demoUser: User = {
  id: "demo-user",
  email: "demo@focusfi.app",
  firstName: "Demo",
  lastName: "User",
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingLog, setLoadingLog] = useState<LoadingLogEntry[]>([]);
  const navigate = useNavigate();
  const addLoadingLog = useCallback(
    (message: string, data?: Record<string, unknown>) => {
      const entry = {
        timestamp: new Date().toISOString(),
        message,
        data,
      };
      setLoadingLog((prev) => [...prev.slice(-49), entry]);
      const key = `auth:loading:${message}:${String(
        (data as { userId?: string | null } | undefined)?.userId ?? ""
      )}`;
      debugThrottled(key, 1500, "[DEBUG] Auth loading", entry);
    },
    []
  );
  const triggerPlaidCacheRefresh = useCallback(
    async (reason: string) => {
      if (demoService.isDemo()) {
        return;
      }
      const sessionKey = "plaid_cache_refreshed_session";
      if (sessionStorage.getItem(sessionKey) === "true") {
        console.debug("[DEBUG] Skipping Plaid refresh (session cached)", {
          reason,
          userId: user?.id,
        });
        return;
      }
      try {
        console.debug("[DEBUG] Triggering Plaid cache refresh", {
          reason,
          userId: user?.id,
        });
        const result = await plaidService.refreshTransactionsCache({
          force: false,
          wait: false,
        });
        void plaidService.refreshAccountsCache();
        console.debug("[DEBUG] Plaid cache refresh result", result);
        sessionStorage.setItem(sessionKey, "true");
      } catch (error) {
        console.error("[DEBUG] Plaid cache refresh failed", error);
      }
    },
    [user?.id]
  );
  const checkUser = useCallback(async () => {
    try {
      addLoadingLog("Auth check started");
      // Check for cached session to show optimistic UI hint (but don't trust it)
      addLoadingLog("Checking cached session");
      const cachedUser = getCachedUser();
      if (cachedUser?.id) {
        addLoadingLog("Found cached user, validating with Supabase", {
          userId: cachedUser.id,
        });
        console.debug("[DEBUG] Found cached user, will validate", {
          userId: cachedUser.id,
          email: cachedUser.email,
        });
        // Don't set user or loading=false here - wait for Supabase validation
      }

      // Always validate session with Supabase (don't trust cache alone)
      addLoadingLog("Fetching session from Supabase");
      const {
        data: { session },
      } = await supabase.auth.getSession();
      let resolvedSession = session;
      if (!resolvedSession) {
        try {
          const {
            data: { session: refreshedSession },
          } = await supabase.auth.refreshSession();
          resolvedSession = refreshedSession;
        } catch (refreshError) {
          console.debug("[DEBUG] Auth check refresh session failed", {
            error:
              refreshError instanceof Error
                ? refreshError.message
                : String(refreshError),
          });
        }
      }
      debugThrottled(
        "auth:supabase-session-check",
        1500,
        "[DEBUG] Supabase session check",
        {
          hasSession: !!resolvedSession,
          userId: resolvedSession?.user?.id,
          email: resolvedSession?.user?.email,
          expiresAt: resolvedSession?.expires_at,
          hadCachedUser: !!cachedUser?.id,
        }
      );
      if (resolvedSession?.user) {
        if (demoService.isDemo()) {
          // Real session takes precedence; prevent demo dataset leakage.
          demoService.setDemoMode(false);
        }
        addLoadingLog("Supabase session valid", {
          userId: resolvedSession.user.id,
        });
        setUser({
          id: resolvedSession.user.id,
          email: resolvedSession.user.email!,
          firstName: resolvedSession.user.user_metadata?.first_name,
          lastName: resolvedSession.user.user_metadata?.last_name,
          avatarUrl: resolvedSession.user.user_metadata?.avatar_url,
        });
        void triggerPlaidCacheRefresh("supabase-session");
      } else {
        if (demoService.isDemo()) {
          addLoadingLog("Demo mode active");
          setUser(demoUser);
          return;
        }
        addLoadingLog("No valid session found");
        // Clear stale cache if localStorage had user but Supabase says no session
        if (cachedUser?.id) {
          addLoadingLog("Clearing stale cached session");
          console.debug("[DEBUG] Clearing stale cached session", {
            cachedUserId: cachedUser.id,
          });
          clearCachedSupabaseSession();
        }
        setUser(null);
      }
    } catch (error) {
      // Ignore AbortError silently - it's expected in React StrictMode
      if (!(error instanceof Error && error.name === "AbortError")) {
        if (
          error instanceof Error &&
          error.message.includes("Invalid Refresh Token")
        ) {
          clearCachedSupabaseSession();
          debugOnce(
            "auth:invalid-refresh-token",
            "[DEBUG] Cleared invalid Supabase refresh token",
            { message: error.message },
            "warn"
          );
          setUser(null);
        } else {
          console.error("Error checking user:", error);
          addLoadingLog("Auth check failed", {
            error: error instanceof Error ? error.message : String(error),
          });
        }
      }
    } finally {
      addLoadingLog("Auth check finished");
      setLoading(false);
    }
  }, [addLoadingLog, triggerPlaidCacheRefresh]);

  useEffect(() => {
    // Check if user is logged in on mount
    addLoadingLog("Auth provider mounted");
    checkUser();

    // Debounce auth state changes to prevent rapid firing (Safari ITP workaround)
    let debounceTimer: NodeJS.Timeout | null = null;
    let lastUserId: string | null = null;

    // Subscribe to auth state changes
    const { data: authListener } = authService.onAuthStateChange((user) => {
      // Skip duplicate consecutive states
      const currentUserId = user?.id || null;
      if (currentUserId === lastUserId) {
        console.debug("[DEBUG] Skipping duplicate auth state", {
          userId: currentUserId,
        });
        return;
      }
      lastUserId = currentUserId;

      // Clear any pending debounced update
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }

      // Debounce the state update to prevent rapid loops
      debounceTimer = setTimeout(() => {
        addLoadingLog("Auth state changed", { userId: user?.id || null });
        debugThrottled(
          `auth:state-change:${user?.id || "none"}`,
          1500,
          "[DEBUG] Auth state change",
          {
            userId: user?.id || null,
            isDemo: demoService.isDemo(),
          }
        );
        if (demoService.isDemo()) {
          setUser(demoUser);
          setLoading(false);
          return;
        }
        if (user && demoService.isDemo()) {
          demoService.setDemoMode(false);
        }
        if (user) {
          setUser(user);
          setLoading(false);
          void triggerPlaidCacheRefresh("auth-change");
          return;
        }

        void (async () => {
          try {
            const {
              data: { session: activeSession },
            } = await supabase.auth.getSession();
            if (activeSession?.user) {
              setUser({
                id: activeSession.user.id,
                email: activeSession.user.email!,
                firstName: activeSession.user.user_metadata?.first_name,
                lastName: activeSession.user.user_metadata?.last_name,
                avatarUrl: activeSession.user.user_metadata?.avatar_url,
              });
              setLoading(false);
              return;
            }
          } catch (verifyError) {
            console.debug("[DEBUG] Auth null-state verification failed", {
              error:
                verifyError instanceof Error
                  ? verifyError.message
                  : String(verifyError),
            });
          }

          const cachedUser = getCachedUser();
          if (cachedUser?.id) {
            console.debug("[DEBUG] Auth state null, clearing stale cache", {
              cachedUserId: cachedUser.id,
            });
            clearCachedSupabaseSession();
          }
          setUser(null);
          setLoading(false);
        })();
      }, 100); // 100ms debounce
    });

    return () => {
      addLoadingLog("Auth provider unmounted");
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      authListener?.subscription.unsubscribe();
    };
  }, [addLoadingLog, checkUser, triggerPlaidCacheRefresh]);

  useEffect(() => {
    const unsubscribe = demoService.subscribe((isDemo) => {
      if (isDemo) {
        supabase.auth.signOut();
        setUser(demoUser);
        setLoading(false);
      } else {
        void checkUser();
      }
    });

    return unsubscribe;
  }, [checkUser]);

  const signUp = async (
    email: string,
    password: string,
    firstName?: string,
    lastName?: string
  ) => {
    const { user, error, requiresEmailConfirmation } = await authService.signUp(
      email,
      password,
      firstName,
      lastName
    );

    console.debug("[DEBUG] Signup result", {
      email,
      requiresEmailConfirmation,
      hasError: Boolean(error),
    });

    if (!error && user && !requiresEmailConfirmation) {
      setUser(user);
      navigate("/dashboard");
    }

    return { error, requiresEmailConfirmation };
  };

  const signIn = async (email: string, password: string) => {
    const { user, error } = await authService.signIn(email, password);

    if (!error && user) {
      if (demoService.isDemo()) {
        demoService.setDemoMode(false);
      }
      setUser(user);
      navigate("/dashboard");
    }

    return { error };
  };

  const signInDemo = async () => {
    await supabase.auth.signOut();
    demoService.setDemoMode(true);
    setUser(demoUser);
    navigate("/dashboard");
    return { error: null };
  };

  const signOut = async () => {
    if (demoService.isDemo()) {
      demoService.setDemoMode(false);
      await supabase.auth.signOut();
      setUser(null);
      navigate("/login");
      return;
    }
    const { error } = await authService.signOut();

    if (!error) {
      setUser(null);
      navigate("/login");
    }
  };

  const updateProfile = async (updates: {
    firstName?: string;
    lastName?: string;
    avatarUrl?: string;
  }) => {
    const { error } = await authService.updateProfile(updates);

    if (!error) {
      // Refresh user data
      const currentUser = await authService.getCurrentUser();
      if (currentUser) {
        setUser(currentUser);
      }
    }

    return { error };
  };

  const resetPassword = async (email: string) => {
    return authService.resetPassword(email);
  };

  const updatePassword = async (newPassword: string) => {
    return authService.updatePassword(newPassword);
  };

  const value = {
    user,
    loading,
    loadingLog,
    signUp,
    signIn,
    signInDemo,
    signOut,
    updateProfile,
    resetPassword,
    updatePassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
